import { type Layer, ZIndexes } from "@common/constants";
import { type Vector } from "@common/utils/vector";
import { Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { type Obstacle } from "../objects/obstacle";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite } from "../utils/pixi";
import { CameraManager } from "./cameraManager";
import { type ImpactKind } from "./combatSoundsLogic";
import { blastMarks, type HoleKind, holeKind, MOST, sameObstacle } from "./damageMarksLogic";
import { markNoNormal } from "./normalLook";

export { BURN_DECALS, blastMarks, holeKind, MOST } from "./damageMarksLogic";

/*
    Damage that stays (0.98.7, the owner, 1 November 2026: "Damage that stays"): what a match does to the world is
    kept until it ends. Every bullet leaves its hole where it struck (a pock in stone and concrete, a splintered hole
    in wood, a dent in steel with its bright rim, a star of cracks in glass, a tear in card, a pit in dirt), on the
    thing it struck, so it goes with the thing when the thing breaks (and comes back with it: the page lets a thing far
    off screen go and makes it again, its holes kept by its number); every blast that burns leaves a scatter of debris
    round its mark, and one with no mark of its own (a barrel's, a stove's) the blast mark's picture, kept for the
    match (the server's marks stay as they are, in 1.01's look); the casings and the chips, splinters and shards the
    shots knock off lie where they fall for the match (managers/combatFx.ts). Each kind is bounded: past its count the
    oldest marks go, quietly.
*/

/** the holes' pictures: this many pixels across (a hole is about half a unit: drawn small) */
const HOLE_PX = 48;
const DEBRIS_PX = 28;
const VARIANTS = 3;

/** a repeatable random for the pictures */
const rng = (seed: number): (() => number) => {
    let s = seed | 0 || 1;
    return () => {
        s = (Math.imul(s, 1664525) + 1013904223) | 0;
        return ((s >>> 0) % 100000) / 100000;
    };
};

const canvasOf = (size: number): { canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D } | undefined => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    return ctx ? { canvas, ctx } : undefined;
};

/** a hole's picture, by its kind and variant */
function holeTexture(kind: HoleKind, variant: number): Texture {
    const made = canvasOf(HOLE_PX);
    if (!made) return Texture.WHITE;
    const { canvas, ctx } = made;
    const r = rng(17 + variant * 131 + kind.length * 7);
    const c = HOLE_PX / 2;
    const jag = (radius: number, points: number, rough: number): void => {
        ctx.beginPath();
        for (let i = 0; i < points; i++) {
            const a = (i / points) * Math.PI * 2;
            const rad = radius * (1 - rough + r() * rough * 2);
            const x = c + Math.cos(a) * rad, y = c + Math.sin(a) * rad;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.closePath();
    };
    switch (kind) {
        case "stone": {
            // the chipped rim, pale, then the pock, dark at its heart
            ctx.fillStyle = "rgba(205,200,190,0.75)";
            jag(c * 0.95, 11, 0.25);
            ctx.fill();
            ctx.fillStyle = "rgba(120,115,105,0.9)";
            jag(c * 0.72, 9, 0.3);
            ctx.fill();
            ctx.fillStyle = "rgba(40,36,32,0.95)";
            jag(c * 0.5, 8, 0.35);
            ctx.fill();
            ctx.strokeStyle = "rgba(50,45,40,0.6)";
            ctx.lineWidth = 1.5;
            for (let i = 0; i < 3; i++) {
                const a = r() * Math.PI * 2;
                ctx.beginPath();
                ctx.moveTo(c + Math.cos(a) * c * 0.4, c + Math.sin(a) * c * 0.4);
                ctx.lineTo(c + Math.cos(a + (r() - 0.5) * 0.6) * c * (0.75 + r() * 0.2), c + Math.sin(a + (r() - 0.5) * 0.6) * c * (0.75 + r() * 0.2));
                ctx.stroke();
            }
            break;
        }
        case "wood": {
            // splinters torn outward, pale fibres, the hole dark
            ctx.fillStyle = "rgba(190,160,110,0.7)";
            for (let i = 0; i < 7; i++) {
                const a = r() * Math.PI * 2;
                const len = c * (0.55 + r() * 0.45);
                ctx.beginPath();
                ctx.moveTo(c + Math.cos(a + 0.25) * c * 0.25, c + Math.sin(a + 0.25) * c * 0.25);
                ctx.lineTo(c + Math.cos(a) * len, c + Math.sin(a) * len);
                ctx.lineTo(c + Math.cos(a - 0.25) * c * 0.25, c + Math.sin(a - 0.25) * c * 0.25);
                ctx.closePath();
                ctx.fill();
            }
            ctx.fillStyle = "rgba(70,50,30,0.9)";
            jag(c * 0.55, 9, 0.4);
            ctx.fill();
            ctx.fillStyle = "rgba(25,18,10,0.95)";
            jag(c * 0.36, 7, 0.4);
            ctx.fill();
            break;
        }
        case "metal": {
            // a dent: a dark hole, its rim bright where the light catches the bent steel
            const halo = ctx.createRadialGradient(c, c, c * 0.2, c, c, c * 0.9);
            halo.addColorStop(0, "rgba(60,60,65,0.5)");
            halo.addColorStop(1, "rgba(60,60,65,0)");
            ctx.fillStyle = halo;
            ctx.fillRect(0, 0, HOLE_PX, HOLE_PX);
            ctx.fillStyle = "rgba(235,235,240,0.85)";
            ctx.beginPath();
            ctx.arc(c - 2, c - 2, c * 0.55, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = "rgba(20,20,24,0.95)";
            jag(c * 0.42, 10, 0.2);
            ctx.fill();
            break;
        }
        case "glass": {
            // a small hole and a star of cracks, with rings between them
            ctx.strokeStyle = "rgba(240,248,255,0.85)";
            ctx.lineWidth = 1.2;
            const rays = 6 + Math.floor(r() * 4);
            for (let i = 0; i < rays; i++) {
                const a = (i / rays) * Math.PI * 2 + (r() - 0.5) * 0.5;
                const len = c * (0.6 + r() * 0.4);
                ctx.beginPath();
                ctx.moveTo(c, c);
                ctx.lineTo(c + Math.cos(a) * len * 0.5, c + Math.sin(a) * len * 0.5 + (r() - 0.5) * 3);
                ctx.lineTo(c + Math.cos(a) * len, c + Math.sin(a) * len);
                ctx.stroke();
            }
            ctx.strokeStyle = "rgba(240,248,255,0.5)";
            ctx.lineWidth = 0.9;
            for (const ring of [0.3, 0.52]) {
                ctx.beginPath();
                for (let i = 0; i <= 14; i++) {
                    const a = (i / 14) * Math.PI * 2;
                    const rad = c * ring * (0.9 + r() * 0.2);
                    const x = c + Math.cos(a) * rad, y = c + Math.sin(a) * rad;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
            }
            ctx.fillStyle = "rgba(30,34,40,0.9)";
            jag(c * 0.22, 7, 0.3);
            ctx.fill();
            break;
        }
        case "card": {
            ctx.fillStyle = "rgba(150,125,90,0.7)";
            jag(c * 0.8, 9, 0.4);
            ctx.fill();
            ctx.fillStyle = "rgba(45,35,22,0.92)";
            jag(c * 0.52, 8, 0.4);
            ctx.fill();
            break;
        }
        case "dirt": {
            ctx.fillStyle = "rgba(90,70,45,0.7)";
            jag(c * 0.85, 10, 0.3);
            ctx.fill();
            ctx.fillStyle = "rgba(40,30,18,0.9)";
            jag(c * 0.55, 8, 0.35);
            ctx.fill();
            break;
        }
    }
    return Texture.from(canvas);
}

/** a piece of debris: a dark shard, grey or brown */
function debrisTexture(variant: number): Texture {
    const made = canvasOf(DEBRIS_PX);
    if (!made) return Texture.WHITE;
    const { canvas, ctx } = made;
    const r = rng(331 + variant * 53);
    const c = DEBRIS_PX / 2;
    const grey = variant % 2 === 0;
    const base = grey ? 70 + Math.floor(r() * 30) : 60 + Math.floor(r() * 20);
    ctx.fillStyle = grey ? `rgb(${base},${base},${base + 4})` : `rgb(${base},${base - 15},${base - 30})`;
    ctx.beginPath();
    const points = 5 + Math.floor(r() * 3);
    for (let i = 0; i < points; i++) {
        const a = (i / points) * Math.PI * 2;
        const rad = c * (0.45 + r() * 0.5);
        const x = c + Math.cos(a) * rad, y = c + Math.sin(a) * rad * 0.7;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    // a lit edge on its top left
    ctx.strokeStyle = "rgba(255,255,255,0.25)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(c - c * 0.5, c + c * 0.1);
    ctx.lineTo(c - c * 0.2, c - c * 0.5);
    ctx.lineTo(c + c * 0.3, c - c * 0.45);
    ctx.stroke();
    return Texture.from(canvas);
}

/** a hole as it's drawn on its thing: its picture, and its place, turn and size in the thing's own coordinates */
interface HoleSpec {
    readonly kind: HoleKind
    readonly variant: number
    readonly x: number
    readonly y: number
    readonly rotation: number
    readonly width: number
    readonly height: number
    readonly alpha: number
}

/** a hole kept: its thing's number, how it's drawn, its sprite while the thing is on screen */
interface HoleEntry {
    readonly id: number
    readonly spec: HoleSpec
    sprite?: Sprite
}

/**
 * the holes of one thing, kept by its number for the match: the page lets a thing go when it's far off screen and
 * makes it again when it comes back, so its holes are kept here and drawn on it again (`owner` the thing drawing them
 * now, none while it's away)
 */
interface HoleRecord {
    readonly idString: string
    readonly layer: number
    x: number
    y: number
    readonly entries: HoleEntry[]
    owner?: Obstacle
}

interface Mark { readonly sprite: Sprite }

class DamageMarksClass {
    private readonly _holeTextures = new Map<string, Texture>();
    private readonly _debrisTextures: Texture[] = [];
    /** every hole kept, the oldest first (drawn or kept for a thing off screen) */
    private _holes: HoleEntry[] = [];
    private readonly _records = new Map<number, HoleRecord>();
    private _scorches: Mark[] = [];
    private _debris: Mark[] = [];
    /** the marks kept now (tests and the lab): the holes drawn and kept, the scorches, the debris */
    get counts(): { holes: number, drawn: number, scorches: number, debris: number } {
        let drawn = 0;
        for (const hole of this._holes) if (hole.sprite && !hole.sprite.destroyed) drawn++;
        return { holes: this._holes.length, drawn, scorches: this._scorches.length, debris: this._debris.length };
    }

    private _holeTexture(kind: HoleKind, variant: number): Texture {
        const key = `${kind}:${variant}`;
        let texture = this._holeTextures.get(key);
        if (!texture || texture.destroyed) {
            texture = holeTexture(kind, variant);
            this._holeTextures.set(key, texture);
        }
        return texture;
    }

    private _debrisTexture(): Texture {
        const variant = Math.floor(Math.random() * 4);
        let texture = this._debrisTextures[variant];
        if (!texture || texture.destroyed) texture = this._debrisTextures[variant] = debrisTexture(variant);
        return texture;
    }

    /** a hole's sprite on its thing's container */
    private _draw(obstacle: Obstacle, spec: HoleSpec): Sprite {
        const sprite = new Sprite(this._holeTexture(spec.kind, spec.variant));
        sprite.anchor.set(0.5);
        sprite.position.set(spec.x, spec.y);
        sprite.rotation = spec.rotation;
        sprite.width = spec.width;
        sprite.height = spec.height;
        sprite.alpha = spec.alpha;
        sprite.eventMode = "none";
        markNoNormal(sprite);
        obstacle.container.addChild(sprite);
        return sprite;
    }

    /** where a thing is now, as its holes' record keeps it */
    private _where(obstacle: Obstacle): { idString: string, layer: number, x: number, y: number } {
        return { idString: obstacle.definition.idString, layer: obstacle.layer, x: obstacle.position.x, y: obstacle.position.y };
    }

    /**
     * a bullet struck `obstacle` at `position` (game units), the surface facing `normal`, made of `kind`: its hole,
     * on the obstacle's own picture (it turns, shrinks and goes with it, and is drawn on it again when it comes back on
     * screen); `damage` the bullet's, for the hole's size. None on a door (its picture swings, its holes wouldn't)
     */
    hole(obstacle: Obstacle, position: Vector, normal: number, kind: ImpactKind, damage: number): void {
        const holeKindOf = holeKind(kind);
        if (!holeKindOf || obstacle.dead || obstacle.destroyed || !Game.gameStarted || obstacle.definition.isDoor) return;
        const container = obstacle.container;
        // the obstacle's own coordinates: the world's turned and scaled back
        const px = position.x * PIXI_SCALE - container.position.x, py = position.y * PIXI_SCALE - container.position.y;
        const cos = Math.cos(-container.rotation), sin = Math.sin(-container.rotation);
        const sx = container.scale.x || 1, sy = container.scale.y || 1;
        const lx = (px * cos - py * sin) / sx, ly = (px * sin + py * cos) / sy;
        // (a hair in from the surface, so the hole sits on the thing and not beside it)
        const inward = 0.12 * PIXI_SCALE;
        const size = (0.62 + 0.3 * Math.min(1.5, damage / 28)) * PIXI_SCALE / sx;
        const variant = Math.floor(Math.random() * VARIANTS);
        const spec: HoleSpec = {
            kind: holeKindOf,
            variant,
            x: lx - Math.cos(normal - container.rotation) * inward / sx,
            y: ly - Math.sin(normal - container.rotation) * inward / sy,
            rotation: holeKindOf === "wood" ? normal - container.rotation + (Math.random() - 0.5) * 0.6 : Math.random() * Math.PI * 2,
            width: size,
            height: size * (holeKindOf === "wood" ? 1.3 : 1),
            alpha: holeKindOf === "glass" ? 0.9 : 0.95
        };
        // its record: this thing's (it may have moved since: the Gravity Gloves), or a new one (a number given anew)
        const now = this._where(obstacle);
        let record = this._records.get(obstacle.id);
        if (record && record.owner !== obstacle && !sameObstacle(record, now)) {
            this._forget(obstacle.id);
            record = undefined;
        }
        if (!record) {
            record = { ...now, entries: [], owner: obstacle };
            this._records.set(obstacle.id, record);
        }
        record.owner = obstacle;
        record.x = now.x;
        record.y = now.y;
        const entry: HoleEntry = { id: obstacle.id, spec, sprite: this._draw(obstacle, spec) };
        record.entries.push(entry);
        this._holes.push(entry);
        if (record.entries.length > MOST.holesOnOne) this._drop(record.entries[0]);
        while (this._holes.length > MOST.holes) this._drop(this._holes[0]);
    }

    /** a hole gone: its sprite, its place in its record and in the count */
    private _drop(entry: HoleEntry): void {
        if (entry.sprite && !entry.sprite.destroyed) entry.sprite.destroy();
        entry.sprite = undefined;
        const index = this._holes.indexOf(entry);
        if (index >= 0) this._holes.splice(index, 1);
        const record = this._records.get(entry.id);
        if (!record) return;
        const at = record.entries.indexOf(entry);
        if (at >= 0) record.entries.splice(at, 1);
        if (record.entries.length === 0) this._records.delete(entry.id);
    }

    /** every hole of the thing numbered `id` gone */
    private _forget(id: number): void {
        const record = this._records.get(id);
        if (!record) return;
        for (const entry of record.entries) {
            if (entry.sprite && !entry.sprite.destroyed) entry.sprite.destroy();
            entry.sprite = undefined;
        }
        const gone = new Set(record.entries);
        this._holes = this._holes.filter(entry => !gone.has(entry));
        this._records.delete(id);
    }

    /** the obstacle broke: its holes go with it (its residue is whole) */
    clearHoles(obstacle: Obstacle): void {
        const record = this._records.get(obstacle.id);
        if (!record || (record.owner !== undefined && record.owner !== obstacle)) return;
        this._forget(obstacle.id);
    }

    /** the page let the obstacle go (far off screen, standing): its holes kept for when it comes back */
    stashHoles(obstacle: Obstacle): void {
        const record = this._records.get(obstacle.id);
        if (!record || record.owner !== obstacle) return;
        record.x = obstacle.position.x;
        record.y = obstacle.position.y;
        for (const entry of record.entries) {
            if (entry.sprite && !entry.sprite.destroyed) entry.sprite.destroy();
            entry.sprite = undefined;
        }
        record.owner = undefined;
    }

    /**
     * the obstacle made again (back on screen): its holes drawn on it as they were; forgotten if it broke while it
     * was away, or if its number is another thing's now
     */
    restoreHoles(obstacle: Obstacle): void {
        const record = this._records.get(obstacle.id);
        if (!record || record.owner === obstacle) return;
        if (obstacle.dead || (record.owner !== undefined && !record.owner.destroyed) || !sameObstacle(record, this._where(obstacle))) {
            if (record.owner === undefined || record.owner.destroyed) this._forget(obstacle.id);
            return;
        }
        for (const entry of record.entries) entry.sprite = this._draw(obstacle, entry.spec);
        record.owner = obstacle;
    }

    /**
     * a blast at `position` (game units) on `layer`, its own mark `decal` (the server's), reaching `reach` units: what
     * `blastMarks` says it leaves. Its debris thrown about it; and a blast with no mark of its own (a barrel's, a
     * stove's) gets the blast mark's picture put down by the page, kept for the match: the server's marks are kept as
     * they are, in 1.01's look (objects/decal.ts). Nothing for a smoke grenade, a seed, a match
     */
    blast(position: Vector, layer: Layer, decal: string | undefined, reach: number): void {
        if (!Game.gameStarted) return;
        const marks = blastMarks(decal, reach);
        if (!marks) return;
        const parent = CameraManager.getContainer(layer);
        if (marks.picture !== undefined && marks.scale !== undefined) {
            const mark = new TrenchioSprite(marks.picture);
            mark.anchor.set(0.5);
            mark.position.set(position.x * PIXI_SCALE, position.y * PIXI_SCALE);
            mark.rotation = Math.random() * Math.PI * 2;
            mark.scale.set(marks.scale);
            mark.zIndex = ZIndexes.Decals;
            mark.eventMode = "none";
            markNoNormal(mark);
            parent.addChild(mark);
            this._scorches.push({ sprite: mark });
            while (this._scorches.length > MOST.scorches) this._gone(this._scorches.shift());
        }
        // the debris thrown about it, most within the burn, a few past its edge
        const pieces = 4 + Math.floor(Math.random() * 6);
        for (let i = 0; i < pieces; i++) {
            const a = Math.random() * Math.PI * 2, d = marks.debris * (0.45 + Math.random() * 1.15) * PIXI_SCALE;
            const piece = new Sprite(this._debrisTexture());
            piece.anchor.set(0.5);
            piece.position.set(position.x * PIXI_SCALE + Math.cos(a) * d, position.y * PIXI_SCALE + Math.sin(a) * d);
            piece.rotation = Math.random() * Math.PI * 2;
            const size = (0.18 + Math.random() * 0.22) * PIXI_SCALE;
            piece.width = size * (1 + Math.random() * 0.6);
            piece.height = size;
            piece.zIndex = ZIndexes.DeadObstacles + 0.3;
            piece.eventMode = "none";
            markNoNormal(piece);
            parent.addChild(piece);
            this._debris.push({ sprite: piece });
        }
        while (this._debris.length > MOST.debris) this._gone(this._debris.shift());
    }

    private _gone(mark: Mark | undefined): void {
        if (mark && !mark.sprite.destroyed) mark.sprite.destroy();
    }

    /** leaving a match: every mark gone, the kept holes too */
    reset(): void {
        for (const hole of this._holes) if (hole.sprite && !hole.sprite.destroyed) hole.sprite.destroy();
        this._holes = [];
        this._records.clear();
        for (const mark of this._scorches) this._gone(mark);
        this._scorches = [];
        for (const mark of this._debris) this._gone(mark);
        this._debris = [];
    }
}

export const DamageMarks = new DamageMarksClass();
